import { Component } from '@angular/core';
import { OnInit, ViewChild } from '@angular/core';

import { AgGridAngular } from 'ag-grid-angular';
import { GridOptions, SortChangedEvent, PaginationChangedEvent, Column } from 'ag-grid-community';

import { AlertsService } from '../../../services/alerts.service';
import { WorkflowQueryService } from '../../services/workflow-query.service';
import { UserService } from '../../../services/user.service';

/**
 * Component for selecting workflow query lists.
 * Displays a grid allowing users to select individual query lists.
 * Grid is implemented using AG Grid
 * API ref: https://www.ag-grid.com/angular-data-grid/grid-api
 * Event API ref: https://www.ag-grid.com/angular-data-grid/grid-events
 * Column properties ref: https://www.ag-grid.com/angular-data-grid/column-properties
 */
@Component({
  selector: 'workflow-querylist-selector',
  templateUrl: './querylist-selector-ag.component.html',
  styleUrl: './querylist-selector-ag.component.css'
})
export class QuerylistSelectorAgComponent implements OnInit {
	@ViewChild('kvGrid') grid!: AgGridAngular;

	LOCALIZATION: any;

	inprogress: boolean = false;

	constraints: any = {};
	pagination = {
		page: 1,
		elementsPerPage: 15,
		elementsPerPageSizes: [15, 25, 100],
		totalElements: 0,
		sorting: {
			defaultSort: 'name,asc',
			sort: ''
		}
	};

	// Properties applied to all columns
	defaultColDef = {
		flex: 1,
		filterParams: {
		  filterOptions: ['contains'],  // Only allow 'contains' filtering
		  defaultOption: 'contains', 
		}		
	};

	columns: any[] = [
		{ field: 'id', hide: true },
		{ field: 'name', headerName: 'Name', sortable: true, filter: true, tooltipField: 'name' },
		{
			field: 'description', headerName: 'Description', sortable: true, filter: true,
			valueGetter: (params: any) => params.data?.description || 'None',
			tooltipValueGetter: (params: any) => params.data?.description || 'None'
		}
	];

	gridOptions: GridOptions = {
		rowSelection: {
			mode: 'multiRow',
			headerCheckbox: true
		}
	};

	gridData: any[] = [];
	selectedRows: any[] = [];

	constructor(
		private alertsService: AlertsService,
		private workflowQueryService: WorkflowQueryService,
		private userService: UserService
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;

		this.pagination.sorting.sort = this.pagination.sorting.defaultSort;
		this.loadQueryLists();
	}

	private loadQueryLists(page?: number): void {
		this.inprogress = true;

		if (page) {
			this.pagination.page = page;
		}

		let index = this.pagination.page - 1;

		this.workflowQueryService.getQueryLists(index, this.pagination, this.constraints).then(
			(data: any) => {
				this.gridData = data.content || [];

				this.pagination.page = data.page.number + 1;
				this.pagination.elementsPerPage = data.page.size;
				this.pagination.totalElements = data.page.totalElements;

				this.inprogress = false;
			},
			(err) => {
				console.error("Failed to load query lists", err);

				this.gridData = [];
				this.inprogress = false;

				this.alertsService.error({
					message: this.LOCALIZATION.noQueryLists
				});
			}
		);
	}

	onGridReady(event: any) {
		this.grid.api.addEventListener('filterChanged', this.onFilterChanged.bind(this));
		this.grid.api.addEventListener('sortChanged', this.onSortChanged.bind(this));
	}

	private onSortChanged(event: SortChangedEvent): void {
		let sortedColumn: Column | undefined = event?.columns?.[0];
		
		if (sortedColumn && sortedColumn.getSort()) {
			this.pagination.sorting.sort = `${sortedColumn.getColId()},${sortedColumn.getSort()}`;
		} else {
			this.pagination.sorting.sort = this.pagination.sorting.defaultSort;
		}

		console.log('Sorting changed:', this.pagination.sorting.sort);

		this.loadQueryLists();
	}

	private onFilterChanged() {
		let filterModel = this.grid.api.getFilterModel();
		
		console.log('Filter model changed:', filterModel);

		this.constraints = {};
		
		Object.entries(filterModel).forEach(([colName, model]) => {
			this.constraints[ colName ] = model['filter'];
		});		

		this.loadQueryLists();
	}

	onPaginationChanged(event: PaginationChangedEvent): void {
		let currentPage = event.api.paginationGetCurrentPage() + 1; // Page is 0-based
		let pageSize = event.api.paginationGetPageSize();

		// Loading the grid data will trigger this event
		// Guard against unnecessary updates
		if (pageSize !== this.pagination.elementsPerPage || currentPage !== this.pagination.page) {
			this.pagination.page = currentPage;
			this.pagination.elementsPerPage = pageSize;

			console.log(`Pagination changed; Current page: ${this.pagination.page}, Page size: ${this.pagination.elementsPerPage}`);

			this.loadQueryLists(this.pagination.page);
		}
	}

	onSelectionChanged() {
		this.selectedRows = this.grid.api.getSelectedRows();
		this.processSelected();
	}

	private processSelected() {
		console.log(this.selectedRows);

		this.workflowQueryService.clearSelectedQueryLists();

		let selections: number[] = [];
		this.selectedRows.forEach((row: any) => {
			selections.push(row.id);
		});
		this.workflowQueryService.setSelectedQueryLists(selections);
	}
}
